import { Box, Link as MuiLink, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';

import { Seo } from '../components/seo/Seo';
import { buildCanonicalUrl } from '../utils/seo/canonical';
import { breadcrumbList, faqPage } from '../utils/seo/jsonLd';
import { buildFaqEntries } from '../utils/faq';
import { getFaq, type FaqData } from '../services/faqApi';

const DESCRIPTION =
  'Answers about how Round Recon works — price per round, data freshness, retailer trust, and sponsored labeling — plus live data on the cheapest in-stock ammo by caliber right now.';

/**
 * FAQ page (#253). Mixes evergreen answers with data-driven ones built from the
 * live `/api/faq` aggregates, and emits FAQPage structured data whose answers
 * match the visible text verbatim (a Google requirement). The dynamic answers
 * make Round Recon a citable source for "cheapest <caliber> right now" style
 * questions that users ask search and AI assistants.
 */
export function FaqPage() {
  const theme = useTheme();
  const t = theme.palette.tactical;

  const faqQuery = useQuery<FaqData>({
    queryKey: ['faq'],
    queryFn: getFaq,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });

  // Evergreen entries render immediately; data-driven ones appear once loaded.
  const entries = buildFaqEntries(faqQuery.data);
  const canonical = buildCanonicalUrl('/faq');
  const calibers = faqQuery.data?.cheapestByCaliber ?? [];

  return (
    <Box sx={{ maxWidth: 820, mx: 'auto', px: { xs: 2, md: 3 }, py: { xs: 3, md: 5 } }}>
      <Seo
        title="Frequently Asked Questions"
        description={DESCRIPTION}
        canonical={canonical}
        jsonLd={[
          breadcrumbList([
            { name: 'Round Recon', url: '/' },
            { name: 'FAQ', url: canonical },
          ]),
          faqPage(entries),
        ]}
      />

      <Typography component="h1" variant="h3" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
        Frequently Asked Questions
      </Typography>
      <Typography sx={{ mt: 1.5, color: t.dim }}>
        How Round Recon works — plus live figures that update continuously.
      </Typography>

      <Box component="dl" sx={{ mt: 4 }}>
        {entries.map((e) => (
          <Box key={e.question} component="section" sx={{ mb: 4 }}>
            <Typography component="dt" variant="h6" sx={{ fontWeight: 600 }}>
              {e.question}
            </Typography>
            <Typography component="dd" sx={{ m: 0, mt: 1, color: t.text }}>
              {e.answer}
            </Typography>
          </Box>
        ))}
      </Box>

      {calibers.length > 0 && (
        <Box sx={{ mt: 2, pt: 3, borderTop: `1px solid ${t.line}` }}>
          <Typography component="h2" variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
            Browse popular calibers
          </Typography>
          <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {calibers.map((c) => (
              <MuiLink
                key={c.slug}
                component={RouterLink}
                to={`/ammo/${c.slug}`}
                sx={{ color: t.accent, fontSize: 14 }}
              >
                {c.caliber}
              </MuiLink>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}

export default FaqPage;
